import type { Config } from '../../_config'
import { DomainError } from '../../_kernel/DomainError'
import { ErrorsCode } from '../../_kernel/ErrorsCode'
import type { FeatureFlag } from '../domain/FeatureFlag'
import { Language } from '../domain/Language'
import { Market } from '../domain/Market'
import type { SharedRepository } from '../domain/SharedRepository'
import type { Source } from '../domain/Source'
import { CookieSharedRepository } from './CookieSharedRepository'
import { DomainSharedRepository } from './DomainSharedRepository'
import { HeaderSharedRepository } from './HeaderSharedRepository'
import { QueryParamSharedRepository } from './QueryParamSharedRepository'

export class SharedRepositoryProxy implements SharedRepository {
  static create({ config }: { config: Config }) {
    return new SharedRepositoryProxy(
      config,
      QueryParamSharedRepository.create({ config }),
      CookieSharedRepository.create({ config }),
      DomainSharedRepository.create({ config }),
      HeaderSharedRepository.create({ config })
    )
  }

  constructor(
    private readonly config: Config,
    private readonly queryParamSharedRepository: QueryParamSharedRepository,
    private readonly cookieSharedRepository: CookieSharedRepository,
    private readonly domainSharedRepository: DomainSharedRepository,
    private readonly headerSharedRepository: HeaderSharedRepository
  ) {}

  async source(): Promise<Source> {
    throw DomainError.withCodes(ErrorsCode.FORBIDDEN_REPOSITORY_METHOD)
  }

  async market() {
    const paramMarket = await this.queryParamSharedRepository.market()

    if (!paramMarket.isEmpty()) return paramMarket

    const domainMarket = await this.domainSharedRepository.market()

    if (!domainMarket.isEmpty()) return domainMarket

    const cookieMarket = await this.cookieSharedRepository.market()

    if (!cookieMarket.isEmpty()) return cookieMarket

    return Market.default()
  }

  async language() {
    const paramLanguage = await this.queryParamSharedRepository.language()

    if (!paramLanguage.isEmpty()) return paramLanguage

    const cookieLanguage = await this.cookieSharedRepository.language()

    if (!cookieLanguage.isEmpty()) return cookieLanguage

    const market = await this.market()
    const marketLanguage = Language.fromMarket(market.value)

    if (!marketLanguage.isEmpty()) return marketLanguage

    const headersLanguage = await this.headerSharedRepository.language()

    if (!headersLanguage.isEmpty()) return headersLanguage

    return Language.default()
  }

  async partialSearch() {
    return this.queryParamSharedRepository.partialSearch()
  }

  async affiliate() {
    return this.cookieSharedRepository.affiliate()
  }

  async featureFlagByName(): Promise<FeatureFlag> {
    throw DomainError.withCodes(ErrorsCode.FORBIDDEN_REPOSITORY_METHOD)
  }
}
